import { parseSegmentsWithDateTags } from "./parseBoldMarkers";

// jsPDF/autotable cells only ever draw a single plain string per cell -
// there's no built-in way to mix bold and normal runs on the same line the
// way react-pdf's nested <Text> (see careplan-view-pdf/CarePlanAuditReportPDF.jsx)
// or the HTML views (renderBoldText) can. This does the word-wrapping and
// drawing manually: tokenize the "**bold**"-marked text into words tagged
// bold/plain, greedily pack them into lines against the cell's own width
// (using each word's own font for its width, so a run of bold words wraps
// correctly even though Helvetica-Bold is slightly wider than normal), then
// draw each line left-to-right, switching font/color per word. Used via
// autoTable's didParseCell (to reserve the right cell height up front,
// before layout) and didDrawCell (to actually draw) - see addSectionTable's
// "richText" cell marker in FinalizeCareplanPDF.jsx, alongside its existing
// "checkbox" cell marker.

// Explicit "\n" (e.g. each "• item" line in a Recommendations list) must
// force its own line - treating it as just more collapsible whitespace,
// the same as a plain space, would run every bullet together into one
// paragraph.
// SCRUM-788: a bold segment carrying a "(added MM/DD/YYYY)"/"(updated
// MM/DD/YYYY)" date tag (parseSegmentsWithDateTags, split out of the bold
// text itself - see parseBoldMarkers.js) gets its tag rendered as its own
// smaller, distinctly-colored run of tokens rather than as bold text, same
// intent as the on-screen DeltaDateTag chip but drawn as plain smaller text
// since jsPDF's manual per-word drawing here has no cheap way to paint a
// rounded chip background per line-wrapped run.
const tokenize = (text) => {
  const tokens = [];
  parseSegmentsWithDateTags(text || "").forEach((segment) => {
    segment.text.split(/(\s+)/).forEach((part) => {
      if (part === "") return;
      const isSpace = /^\s+$/.test(part);
      // SCRUM-838: a run of TWO consecutive newlines (a blank line between
      // paragraphs) landed in one whitespace token here ("\n\n") - counting
      // it as a single break collapsed the blank line entirely instead of
      // preserving the visual gap the user actually typed. breakCount below
      // pushes one empty line per "\n" in the run, not just one per token.
      const breakCount = isSpace ? (part.match(/\n/g) || []).length : 0;
      tokens.push({ text: part, bold: segment.bold, isSpace, breakCount });
    });
    if (segment.dateTag) {
      const tagText = `(${segment.dateTag.kind} ${segment.dateTag.date})`;
      tokens.push({ text: " ", bold: false, isSpace: true, breakCount: 0 });
      tokens.push({ text: tagText, bold: false, isSpace: false, breakCount: 0, isDateTag: true });
    }
  });
  return tokens;
};

const wrapTokensIntoLines = (pdf, tokens, maxWidth, fontSize, fontName, dateTagFontSize) => {
  const lines = [[]];
  let lineWidth = 0;
  tokens.forEach((token) => {
    if (token.breakCount) {
      for (let i = 0; i < token.breakCount; i++) lines.push([]);
      lineWidth = 0;
      return;
    }
    pdf.setFont(fontName, token.bold ? "bold" : "normal");
    pdf.setFontSize(token.isDateTag ? dateTagFontSize : fontSize);
    const width = pdf.getTextWidth(token.text);
    if (!token.isSpace && lineWidth + width > maxWidth && lineWidth > 0) {
      lines.push([]);
      lineWidth = 0;
    }
    lines[lines.length - 1].push({ ...token, width });
    lineWidth += width;
  });
  return lines;
};

const DEFAULTS = {
  lineHeight: 4.2, // mm, approximates a 10pt font's line height at this doc's "mm" unit
  fontSize: 10,
  fontName: "helvetica",
  normalColor: [51, 51, 51],
  boldColor: [0, 0, 0], // black per direct request (was #6b7280 grey)
  dateTagFontSize: 7,
  dateTagColor: [67, 56, 202], // #4338CA, matches the on-screen DeltaDateTag chip
};

export const measureBoldAwareCellHeight = (pdf, text, options = {}) => {
  const { maxWidth, lineHeight, fontSize, fontName, dateTagFontSize, minLines = 1 } = {
    ...DEFAULTS,
    ...options,
  };
  const lines = wrapTokensIntoLines(pdf, tokenize(text), maxWidth, fontSize, fontName, dateTagFontSize);
  return Math.max(lines.length, minLines) * lineHeight;
};

// Line-level API for callers that lay text out themselves (e.g. to move
// to a new page between lines): wrap once, then draw one line at a time.
export const layoutBoldAwareLines = (pdf, text, options = {}) => {
  const { maxWidth, fontSize, fontName, dateTagFontSize } = { ...DEFAULTS, ...options };
  return wrapTokensIntoLines(pdf, tokenize(text), maxWidth, fontSize, fontName, dateTagFontSize);
};

export const drawBoldAwareLine = (pdf, lineTokens, x, y, options = {}) => {
  const { fontSize, fontName, normalColor, boldColor, dateTagFontSize, dateTagColor } = {
    ...DEFAULTS,
    ...options,
  };
  let cursorX = x;
  let atLineStart = true;
  lineTokens.forEach((token) => {
    if (token.isSpace) {
      if (!atLineStart) cursorX += token.width;
      return;
    }
    atLineStart = false;
    pdf.setFont(fontName, token.bold ? "bold" : "normal");
    pdf.setFontSize(token.isDateTag ? dateTagFontSize : fontSize);
    pdf.setTextColor(...(token.isDateTag ? dateTagColor : token.bold ? boldColor : normalColor));
    pdf.text(token.text, cursorX, y);
    cursorX += token.width;
  });

  // Reset so drawing after this line isn't left on a stray font/size/color.
  pdf.setFont(fontName, "normal");
  pdf.setFontSize(fontSize);
  pdf.setTextColor(...normalColor);
};

export const drawBoldAwareCellText = (pdf, text, x, y, options = {}) => {
  const { lineHeight } = { ...DEFAULTS, ...options };
  layoutBoldAwareLines(pdf, text, options).forEach((lineTokens, i) => {
    drawBoldAwareLine(pdf, lineTokens, x, y + i * lineHeight, options);
  });
};
